import { useState } from 'react';
import { Clock, User, FileText, CalendarCheck } from 'lucide-react';
import { palette, transition } from '../../styles/tokens';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import type { ScheduleEntry } from '../../types';
import { nameGradient, initials, fmtTime12, entryStartMin, entryEndMin, entryDurationMin } from './appointmentUtils';
import { instantToBusinessParts } from '../../components/schedule/scheduleHelpers';

interface Props {
  entry: ScheduleEntry | null;
  operatorName: string;
  onClose: () => void;
  onAdvance: (entryId: string, bookingId: string, status: string) => void;
}

const STATUS_BADGES: Record<string, { label: string; bg: string; fg: string }> = {
  scheduled: { label: 'Confirmed', bg: '#EFF6FF', fg: '#3B82F6' },
  confirmed: { label: 'Confirmed', bg: '#EFF6FF', fg: '#3B82F6' },
  pending: { label: 'Pending', bg: '#FFFBEB', fg: '#D97706' },
  in_progress: { label: 'In Progress', bg: '#FFF7ED', fg: '#CA5010' },
  completed: { label: 'Completed', bg: '#F0FDF4', fg: '#16A34A' },
  no_show: { label: 'No Show', bg: '#FEF2F2', fg: '#DC2626' },
  cancelled: { label: 'Cancelled', bg: '#F3F4F6', fg: '#6B7280' },
};

export function AppointmentDetailPanel({ entry, operatorName, onClose, onAdvance }: Props) {
  const [confirmAction, setConfirmAction] = useState<{ status: string; title: string; message: string } | null>(null);

  if (!entry) return null;

  const titleParts = entry.title?.split(' — ') ?? [entry.title ?? ''];
  const serviceName = titleParts.length > 1 ? titleParts[0] : 'Service';
  const clientName = titleParts.length > 1 ? titleParts[1] : titleParts[0];
  const startMin = entryStartMin(entry);
  const endMin = entryEndMin(entry);
  const duration = entryDurationMin(entry);
  // A2 P4 — date label of a stored instant in the business tz, not device tz
  // (a late-evening entry must not show the next day's date on an eastward device).
  const dp = instantToBusinessParts(entry.start_at);
  const dateStr = new Date(Date.UTC(dp.year, dp.month - 1, dp.day, 12))
    .toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
  const status = STATUS_BADGES[entry.status] ?? STATUS_BADGES.scheduled;
  const bookingId = entry.booking_id;
  const [g1, g2] = nameGradient(clientName);

  return (
    <SlidePanel isOpen={!!entry} onClose={onClose} title="Appointment" width={380}>
      {/* Client header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 22,
          background: `linear-gradient(135deg, ${g1}, ${g2})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 15, fontWeight: 600, color: '#fff',
        }}>
          {initials(clientName)}
        </div>
        <div>
          <div style={{ fontSize: 17, fontWeight: 600, color: palette.g800 }}>{clientName}</div>
        </div>
      </div>

      {/* Info sections */}
      <InfoRow icon={<CalendarCheck size={14} color={palette.g400} />} label="Service" value={serviceName} sub={`${duration} minutes`} />
      <InfoRow icon={<Clock size={14} color={palette.g400} />} label="Time" value={`${fmtTime12(startMin)} — ${fmtTime12(endMin)}`} sub={dateStr} />
      <InfoRow icon={<User size={14} color={palette.g400} />} label="Operator" value={operatorName} />
      {entry.notes && <InfoRow icon={<FileText size={14} color={palette.g400} />} label="Notes" value={entry.notes} />}

      {/* Status badge */}
      <div style={{ marginTop: 20, marginBottom: 16 }}>
        <span style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: status.bg, color: status.fg }}>
          {status.label}
        </span>
      </div>

      {/* Primary actions */}
      {bookingId && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {(entry.status === 'pending' || entry.status === 'scheduled') && (
            <ActionBtn label="Start Service" accent onClick={() => onAdvance(entry.id, bookingId, 'in_progress')} />
          )}
          {entry.status === 'confirmed' && (
            <ActionBtn label="Start Service" accent onClick={() => onAdvance(entry.id, bookingId, 'in_progress')} />
          )}
          {entry.status === 'in_progress' && (
            <ActionBtn label="Complete" accent onClick={() => onAdvance(entry.id, bookingId, 'completed')} />
          )}
        </div>
      )}

      {/* Secondary actions */}
      {bookingId && (
        <div style={{ borderTop: `1px solid ${palette.g100}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {entry.status !== 'completed' && entry.status !== 'cancelled' && entry.status !== 'no_show' && (
            <>
              <SecondaryBtn label="Reschedule" onClick={() => { onClose(); }} />
              <SecondaryBtn label="Reassign" onClick={() => { onClose(); }} />
              <SecondaryBtn label="Mark No-Show" onClick={() => setConfirmAction({ status: 'no_show', title: 'Mark as no-show?', message: 'This booking will be marked as a no-show.' })} destructive />
              <SecondaryBtn label="Cancel Booking" onClick={() => setConfirmAction({ status: 'cancelled', title: 'Cancel this booking?', message: 'This booking will be cancelled.' })} destructive />
            </>
          )}
        </div>
      )}

      {/* Meta */}
      <div style={{ marginTop: 20, paddingTop: 12, borderTop: `1px solid ${palette.g100}`, fontSize: 11, color: palette.g400 }}>
        {entry.created_at && <div>Created: {new Date(entry.created_at).toLocaleString('en-CA', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</div>}
      </div>
      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmAction?.title ?? ''}
        message={confirmAction?.message ?? ''}
        confirmLabel={confirmAction?.status === 'no_show' ? 'Mark No-Show' : 'Cancel Booking'}
        confirmVariant="danger"
        onClose={() => setConfirmAction(null)}
        onConfirm={() => { if (confirmAction && bookingId) { onAdvance(entry.id, bookingId, confirmAction.status); setConfirmAction(null); } }}
      />
    </SlidePanel>
  );
}

function InfoRow({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px 0', borderBottom: `1px solid ${palette.g100}` }}>
      <div style={{ marginTop: 2 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 11, color: palette.g400, marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 500, color: palette.g800 }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: palette.g500, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}

function ActionBtn({ label, accent, onClick }: { label: string; accent?: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} style={{
      flex: 1, padding: '10px 16px', borderRadius: 8, border: 'none',
      background: accent ? palette.accent : palette.g100,
      color: accent ? palette.white : palette.g700,
      fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
    }}>{label}</button>
  );
}

function SecondaryBtn({ label, onClick, destructive }: { label: string; onClick: () => void; destructive?: boolean }) {
  return (
    <button onClick={onClick} style={{
      width: '100%', padding: '9px 12px', borderRadius: 6, border: 'none',
      background: 'transparent', textAlign: 'left',
      fontSize: 13, color: destructive ? palette.red : palette.g700,
      cursor: 'pointer', fontFamily: 'inherit',
      transition: `background ${transition.fast}`,
    }}
      onMouseEnter={e => (e.currentTarget.style.background = palette.g50)}
      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
    >{label}</button>
  );
}
